/**
 * /akun/pin — PIN setup / verify screen (Phase 5.2).
 *
 * State machine:
 *   - mode='loading' → check whether a PIN already exists
 *   - mode='verify'  → user enters PIN, we POST /v1/parent/auth
 *                        - 200 → save session, navigate to /akun/parent-zone
 *                        - 401 → show wrong_pin, increment local counter
 *                        - 423 → show cooldown (30s/5min/permanent)
 *   - mode='setup'   → user enters PIN twice (input + confirm)
 *                        - match → POST /v1/parent/pin, then auto-auth
 *                        - mismatch → "PIN tidak cocok, coba lagi"
 *                        - forbidden (0000, 1234, dll) → show hint
 *   - mode='locked'  → countdown timer, "Coba lagi dalam 25 dtk"
 *
 * Y1 simplification: no biometric, no Lupa PIN flow yet (5.4 will add
 * the "Hapus semua data" path).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import { Brand, Radii, Spacing } from '@/constants/theme';
import { PinBoxes } from '@/components/parent/PinBoxes';
import { PinPad } from '@/components/parent/PinPad';
import { authPin, getPinStatus, setPin, type AuthPinResult } from '@/lib/api/parent';
import { saveSession } from '@/lib/parent-session';
import { getOrCreateDeviceId } from '@/lib/device-id';
import { PIN_POLICY } from '@/lib/pin-policy';
import { isGuestSession } from '@/lib/auth';

type ScreenState =
  | { mode: 'loading' }
  | { mode: 'error'; message: string }
  | { mode: 'verify'; pin: string; error: string | null; failedAttempts: number }
  | { mode: 'setup'; pin: string; firstPin: string; error: string | null }
  | { mode: 'setup_confirm'; pin: string; firstPin: string; error: string | null }
  | { mode: 'locked'; cooldownSeconds: number; isPermanent: boolean };

const MAX_PIN_LENGTH = PIN_POLICY.LENGTH;

export default function PinScreen() {
  const router = useRouter();
  const [state, setState] = useState<ScreenState>({ mode: 'loading' });
  const [submitting, setSubmitting] = useState(false);
  const [statusCheckAttempt, setStatusCheckAttempt] = useState(0);
  const cooldownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Guest sessions are intentionally frictionless: there is no account PIN
  // to protect, so they enter Profil dan Preferensi directly while exploring.
  useEffect(() => {
    let active = true;

    const detectPinState = async () => {
      if (isGuestSession()) {
        router.replace('/akun/parent-zone' as never);
        return;
      }

      const result = await getPinStatus();
      if (!active) return;
      if (!result.ok) {
        setState({
          mode: 'error',
          message: result.message ?? 'Gagal mengecek status PIN.',
        });
        return;
      }

      setState(
        result.hasPin
          ? { mode: 'verify', pin: '', error: null, failedAttempts: 0 }
          : { mode: 'setup', pin: '', firstPin: '', error: null },
      );
    };

    void detectPinState();
    return () => {
      active = false;
    };
  }, [router, statusCheckAttempt]);

  // --------------------------------------------------------------------------
  // Cooldown countdown
  // --------------------------------------------------------------------------

  useEffect(() => {
    // Narrow the type: when state.mode is 'locked', isPermanent is available.
    if (state.mode !== 'locked') return;
    const isPerm = state.isPermanent;
    if (isPerm) return;
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    cooldownTimerRef.current = setInterval(() => {
      setState((prev) => {
        if (prev.mode !== 'locked' || prev.isPermanent) return prev;
        if (prev.cooldownSeconds <= 1) {
          if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
          cooldownTimerRef.current = null;
          return { mode: 'verify', pin: '', error: null, failedAttempts: 0 };
        }
        return { ...prev, cooldownSeconds: prev.cooldownSeconds - 1 };
      });
    }, 1000);
    return () => {
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    };
  }, [state.mode, state.mode === 'locked' ? state.isPermanent : false]);

  // --------------------------------------------------------------------------
  // Input handlers
  // --------------------------------------------------------------------------

  const onDigit = useCallback((digit: string) => {
    setState((prev) => {
      if (prev.mode !== 'verify' && prev.mode !== 'setup' && prev.mode !== 'setup_confirm') {
        return prev;
      }
      if (prev.pin.length >= MAX_PIN_LENGTH) return prev;
      return { ...prev, pin: prev.pin + digit, error: null };
    });
  }, []);

  const onBackspace = useCallback(() => {
    setState((prev) => {
      if (prev.mode !== 'verify' && prev.mode !== 'setup' && prev.mode !== 'setup_confirm') {
        return prev;
      }
      return { ...prev, pin: prev.pin.slice(0, -1), error: null };
    });
  }, []);

  // --------------------------------------------------------------------------
  // Auto-submit on 4th digit
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (state.mode === 'locked' || state.mode === 'loading' || state.mode === 'error') return;
    if (state.pin.length !== MAX_PIN_LENGTH) return;
    if (submitting) return;
    // From here, mode is 'verify' | 'setup' | 'setup_confirm' (all have `pin`).
    const mode = state.mode;
    const pin = state.pin;
    if (mode === 'verify') {
      void handleVerifySubmit(pin);
    } else if (mode === 'setup') {
      // First entry done — advance to confirm.
      setState({ mode: 'setup_confirm', pin: '', firstPin: pin, error: null });
    } else if (mode === 'setup_confirm') {
      if (pin === state.firstPin) {
        void handleSetupSubmit(state.firstPin);
      } else {
        setState({
          mode: 'setup',
          pin: '',
          firstPin: '',
          error: 'PIN tidak cocok. Coba lagi.',
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.mode,
    state.mode === 'verify' || state.mode === 'setup' || state.mode === 'setup_confirm'
      ? state.pin
      : '',
  ]);

  // --------------------------------------------------------------------------
  // Submit: verify
  // --------------------------------------------------------------------------

  const handleVerifySubmit = useCallback(async (pin: string) => {
    setSubmitting(true);
    try {
      const result = await authPin(pin);
      await onAuthResult(result);
    } finally {
      setSubmitting(false);
    }
  }, []);

  const onAuthResult = useCallback(async (result: AuthPinResult) => {
    if (result.ok) {
      const deviceId = getOrCreateDeviceId();
      await saveSession({
        token: result.sessionToken,
        expiresAt: result.expiresAt,
        deviceId,
      });
      router.replace('/akun/parent-zone' as never);
      return;
    }

    if (result.error === 'no_pin') {
      // No PIN yet — switch to setup mode.
      setState({ mode: 'setup', pin: '', firstPin: '', error: null });
      return;
    }

    if (result.error === 'locked') {
      if (result.isPermanent) {
        setState({ mode: 'locked', cooldownSeconds: 0, isPermanent: true });
      } else {
        setState({
          mode: 'locked',
          cooldownSeconds: result.remainingSeconds ?? 30,
          isPermanent: false,
        });
      }
      return;
    }

    if (result.error === 'wrong_pin') {
      setState((prev) => {
        if (prev.mode !== 'verify') return prev;
        return {
          mode: 'verify',
          pin: '',
          error: result.message ?? 'PIN salah.',
          failedAttempts: result.failedAttempts ?? prev.failedAttempts + 1,
        };
      });
      return;
    }

    // forbidden_pin / unknown — treat as bad input.
    setState((prev) => {
      if (prev.mode !== 'verify') return prev;
      return {
        mode: 'verify',
        pin: '',
        error: result.message ?? 'PIN tidak valid.',
        failedAttempts: prev.failedAttempts,
      };
    });
  }, [router]);

  // --------------------------------------------------------------------------
  // Submit: setup (after confirm)
  // --------------------------------------------------------------------------

  const handleSetupSubmit = useCallback(async (pin: string) => {
    setSubmitting(true);
    try {
      const result = await setPin(pin);
      if (!result.ok) {
        setState({
          mode: 'setup',
          pin: '',
          firstPin: '',
          error: result.message ?? 'Gagal menyimpan PIN.',
        });
        return;
      }
      // Auto-auth after successful set.
      const auth = await authPin(pin);
      await onAuthResult(auth);
    } finally {
      setSubmitting(false);
    }
  }, [onAuthResult]);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  if (state.mode === 'loading') {
    return (
      <ThemedView style={styles.root}>
        <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
          <View style={styles.center}>
            <ActivityIndicator size="large" color={Brand.primary} />
            <ThemedText type="default" themeColor="textSecondary">
              Mengecek pengaturan Profil dan Preferensi…
            </ThemedText>
          </View>
        </SafeAreaView>
      </ThemedView>
    );
  }

  if (state.mode === 'error') {
    return (
      <ThemedView style={styles.root}>
        <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
          <View style={styles.center}>
            <ThemedText type="subtitle" style={styles.headerTitle}>
              Belum bisa membuka Profil dan Preferensi
            </ThemedText>
            <Text style={styles.errorText}>{state.message}</Text>
            <Pressable
              onPress={() => setStatusCheckAttempt((value) => value + 1)}
              style={({ pressed }) => [styles.btnSecondary, pressed && styles.btnPressed]}
            >
              <Text style={styles.btnSecondaryText}>Coba lagi</Text>
            </Pressable>
            <Pressable
              onPress={() => router.back()}
              style={({ pressed }) => [styles.btnSecondary, pressed && styles.btnPressed]}
            >
              <Text style={styles.btnSecondaryText}>Kembali</Text>
            </Pressable>
          </View>
        </SafeAreaView>
      </ThemedView>
    );
  }

  if (state.mode === 'locked') {
    return (
      <ThemedView style={styles.root}>
        <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
          <PinHeader
            title={state.isPermanent ? 'PIN Terkunci' : 'Terlalu Banyak Percobaan'}
            subtitle={
              state.isPermanent
                ? 'Uninstall + reinstall aplikasi untuk reset.'
                : 'Kamu bisa coba lagi sebentar lagi.'
            }
          />
          <View style={styles.center}>
            {state.isPermanent ? null : (
              <>
                <Text style={styles.countdown}>{state.cooldownSeconds}</Text>
                <Text style={styles.countdownLabel}>detik</Text>
              </>
            )}
          </View>
          <View style={styles.bottomActions}>
            <Pressable
              onPress={() => router.back()}
              style={({ pressed }) => [styles.btnSecondary, pressed && styles.btnPressed]}
            >
              <Text style={styles.btnSecondaryText}>Kembali</Text>
            </Pressable>
          </View>
        </SafeAreaView>
      </ThemedView>
    );
  }

  // Setup mode (first PIN input)
  if (state.mode === 'setup') {
    return (
      <ThemedView style={styles.root}>
        <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
          <PinHeader
            title="Buat PIN Profil dan Preferensi"
            subtitle="PIN ini menjaga pengaturan orang tua dari anak-anak."
          />
          <View style={styles.center}>
            <PinBoxes length={state.pin.length} total={MAX_PIN_LENGTH} />
            {state.error ? <Text style={styles.errorText}>{state.error}</Text> : null}
            <Text style={styles.hint}>
              Pilih 4 digit angka (bukan 0000, 1234, atau pola berulang)
            </Text>
            <View style={styles.padWrap}>
              <PinPad
                onDigit={onDigit}
                onBackspace={onBackspace}
                disabled={submitting}
              />
            </View>
          </View>
          <View style={styles.bottomActions}>
            <Pressable
              onPress={() => router.back()}
              style={({ pressed }) => [styles.btnSecondary, pressed && styles.btnPressed]}
            >
              <Text style={styles.btnSecondaryText}>Batal</Text>
            </Pressable>
          </View>
        </SafeAreaView>
      </ThemedView>
    );
  }

  // Setup confirm (re-enter PIN)
  if (state.mode === 'setup_confirm') {
    return (
      <ThemedView style={styles.root}>
        <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
          <PinHeader
            title="Konfirmasi PIN"
            subtitle="Masukkan PIN yang sama sekali lagi."
          />
          <View style={styles.center}>
            <PinBoxes length={state.pin.length} total={MAX_PIN_LENGTH} />
            {state.error ? <Text style={styles.errorText}>{state.error}</Text> : null}
            <Text style={styles.hint}>
              Pastikan PIN sama. Kalau beda, coba dari awal lagi.
            </Text>
            <View style={styles.padWrap}>
              <PinPad
                onDigit={onDigit}
                onBackspace={onBackspace}
                disabled={submitting}
              />
            </View>
          </View>
          <View style={styles.bottomActions}>
            <Pressable
              onPress={() => setState({ mode: 'setup', pin: '', firstPin: '', error: null })}
              style={({ pressed }) => [styles.btnSecondary, pressed && styles.btnPressed]}
            >
              <Text style={styles.btnSecondaryText}>Ulangi dari awal</Text>
            </Pressable>
          </View>
        </SafeAreaView>
      </ThemedView>
    );
  }

  // Verify mode
  return (
    <ThemedView style={styles.root}>
      <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
        <PinHeader
          title="Masukkan PIN"
          subtitle="PIN menjaga Profil dan Preferensi dari anak-anak."
        />
        <View style={styles.center}>
          <PinBoxes length={state.pin.length} total={MAX_PIN_LENGTH} />
          {state.error ? (
            <Text style={styles.errorText}>
              {state.error}
              {state.failedAttempts > 0
                ? ` (${state.failedAttempts}/10 percobaan)`
                : ''}
            </Text>
          ) : null}
          <Text style={styles.hint}>
            4 digit angka. Tepat. Aman.
          </Text>
          <View style={styles.padWrap}>
            <PinPad
              onDigit={onDigit}
              onBackspace={onBackspace}
              disabled={submitting}
            />
          </View>
        </View>
        <View style={styles.bottomActions}>
          <Pressable
            onPress={() => router.back()}
            style={({ pressed }) => [styles.btnSecondary, pressed && styles.btnPressed]}
          >
            <Text style={styles.btnSecondaryText}>Batal</Text>
          </Pressable>
        </View>
      </SafeAreaView>
    </ThemedView>
  );
}

// ----------------------------------------------------------------------------
// PinHeader
// ----------------------------------------------------------------------------

function PinHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <View style={styles.header}>
      <ThemedText type="subtitle" style={styles.headerTitle}>{title}</ThemedText>
      <ThemedText type="default" themeColor="textSecondary" style={styles.headerSubtitle}>
        {subtitle}
      </ThemedText>
    </View>
  );
}

// ----------------------------------------------------------------------------
// Styles
// ----------------------------------------------------------------------------

const styles = StyleSheet.create({
  root: { flex: 1 },
  safe: { flex: 1 },
  header: {
    alignItems: 'center',
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.three,
    paddingBottom: Spacing.three,
    gap: Spacing.one,
  },
  headerTitle: { fontSize: 20, fontWeight: '700', color: '#1A0B5C', textAlign: 'center' },
  headerSubtitle: { fontSize: 14, textAlign: 'center' },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Spacing.four,
    gap: Spacing.three,
  },
  errorText: { color: '#e53e3e', fontSize: 13, textAlign: 'center' },
  hint: { color: '#6b6573', fontSize: 12, textAlign: 'center' },
  padWrap: { marginTop: Spacing.two },
  countdown: { fontSize: 64, fontWeight: '700', color: Brand.primary, marginTop: Spacing.two },
  countdownLabel: { fontSize: 14, color: '#6b6573' },
  bottomActions: {
    padding: Spacing.four,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  btnSecondary: {
    paddingHorizontal: Spacing.four,
    paddingVertical: Spacing.two,
    borderRadius: Radii.medium,
  },
  btnSecondaryText: { color: Brand.primary, fontSize: 14, fontWeight: '600' },
  btnPressed: { opacity: 0.6 },
});
